import React, { useState, useEffect } from 'react';
import { Settings, LogIn } from 'lucide-react';
import PublicContentManager from './PublicContentManager';
import BlogContentManager from './BlogContentManager';
import UsersDirectory from './UsersDirectory';
import CompanyDirectory from './CompanyDirectory';
import AdminDashboard from './AdminDashboard';
import Login from './Login';
import { backofficeAuth, backofficeDb } from '../backofficeFirebaseClient';
import ApiSettings from './ApiSettings';
import SubscriptionManager from './SubscriptionManager';
import ActivityLogs from './ActivityLogs';
import TopupManager from './TopupManager';
import PlanUpgrade from './PlanUpgrade';
import PaymentRecorder from './PaymentRecorder';
import PaymentHistory from './PaymentHistory';
import SubscriptionMigration from './SubscriptionMigration';
import CollectionInitializer from './CollectionInitializer';
import PlanCatalogManager from './PlanCatalogManager';
import AdminDirectory from './AdminDirectory';
import CouponsManager from './CouponsManager';
import AllocationsManager from './AllocationsManager';
import AdminAuditLogs from './AdminAuditLogs';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';

const Sidebar = ({ active, onChange, role }) => {
  const items = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'admins', label: 'Admins' },
    { id: 'users', label: 'Users' },
    { id: 'companies', label: 'Companies' },
    { id: 'subscription-manager', label: 'Subscription Manager', icon: '🎯' },
    { id: 'plan-catalog', label: 'Plan Catalog', icon: '📋' },
    { id: 'topup-manager', label: 'Top-up Manager', icon: '📦' },
    { id: 'plan-upgrade', label: 'Plan Upgrades', icon: '🚀' },
    { id: 'payment-recorder', label: 'Record Payment', icon: '💰' },
    { id: 'payment-history', label: 'Payment History', icon: '📜' },
    { id: 'subscription-migration', label: 'Migration Tool', icon: '🔄' },
    { id: 'collection-initializer', label: 'Initialize Collections', icon: '🗄️' },
    { id: 'activity-logs', label: 'Activity Logs', icon: '📊' },
    { id: 'coupons', label: 'Coupons' },
    { id: 'allocations', label: 'Allocations' },
    { id: 'public-content', label: 'Public Content' },
    { id: 'blog-content', label: 'Blog Content' },
    { id: 'logs', label: 'Logs' },
    ...(role === 'super_admin' ? [{ id: 'api-settings', label: 'API Settings' }] : [])
  ];
  return (
    <div className="w-full md:w-64 md:min-h-screen border-r bg-white">
      <div className="p-4 text-xl font-semibold">Backoffice</div>
      <nav className="space-y-1 px-2 pb-4">
        {items.map((item) => (
          <button
            key={item.id}
            onClick={() => onChange(item.id)}
            className={`w-full text-left px-3 py-2 rounded flex items-center ${active === item.id ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'}`}
          >
            {item.icon && <span className="mr-2">{item.icon}</span>}
            {item.label}
          </button>
        ))}
      </nav>
    </div>
  );
};

const UsersSection = () => (
  <div className="p-6">
    <UsersDirectory />
  </div>
);

const WebsiteSection = () => {
  const [loading, setLoading] = useState(false);
  const defaultContent = {
    hero: {
      title: 'Smart Accounting for',
      highlight: ' Modern Business',
      subtitle: "Streamline your business finances with India's most comprehensive accounting software.",
      primaryCtaText: 'Start Free Trial',
      secondaryCtaText: 'Watch Demo'
    },
    pricing: {
      basicPrice: 999,
      trialText: 'After 3 months free trial'
    },
    cta: {
      heading: 'Ready to Transform Your Business?',
      subheading: 'Join thousands of businesses that trust Acctoo.',
      primaryCtaText: 'Start Free Trial',
      secondaryCtaText: 'Schedule Demo'
    },
    featuresGrid: [
      { 
        title: "Smart Dashboard", 
        text: "Real-time overview of sales, receivables, payables and tasks.",
        icon: "chart-bar",
        color: "blue",
        keywords: ["dashboard", "analytics", "real-time", "business overview"],
        metrics: [
          { label: "Sales This Month", value: "₹2,45,000", color: "blue" },
          { label: "Outstanding Receivables", value: "₹1,20,000", color: "green" },
          { label: "Pending Tasks", value: "5", color: "orange" }
        ]
      },
      { 
        title: "Sales & Invoicing", 
        text: "Professional invoices, quotations and challans with auto GST.",
        icon: "document-text",
        color: "green",
        keywords: ["invoicing", "GST", "quotations", "professional"],
        metrics: [
          { label: "Invoice Templates", value: "Professional", color: "green" },
          { label: "GST Calculation", value: "Auto", color: "blue" },
          { label: "Payment Modes", value: "Multiple", color: "purple" }
        ]
      },
      { 
        title: "Purchase Management", 
        text: "POs, bills and vendor tracking with full history.",
        icon: "shopping-bag",
        color: "purple",
        keywords: ["purchase", "vendors", "POs", "bills"],
        metrics: [
          { label: "Purchase Orders", value: "Create & Track", color: "purple" },
          { label: "Vendor Management", value: "Complete", color: "blue" },
          { label: "Bill Tracking", value: "Real-time", color: "green" }
        ]
      },
      { 
        title: "Payment Tracking", 
        text: "Multiple payment modes, receipts and outstanding monitoring.",
        icon: "credit-card",
        color: "orange",
        keywords: ["payments", "receipts", "tracking", "outstanding"],
        metrics: [
          { label: "Payment Modes", value: "Cash, UPI, Bank", color: "orange" },
          { label: "Receipt Generation", value: "Auto", color: "green" },
          { label: "Outstanding Tracking", value: "Real-time", color: "blue" }
        ]
      },
      { 
        title: "Advanced Reports", 
        text: "GST, P&L, Balance Sheet and custom analytics.",
        icon: "chart-pie",
        color: "indigo",
        keywords: ["reports", "GST", "analytics", "financial"],
        metrics: [
          { label: "GST Reports", value: "GSTR-1, GSTR-3B", color: "indigo" },
          { label: "Financial Reports", value: "P&L, Balance Sheet", color: "blue" },
          { label: "Custom Analytics", value: "Advanced", color: "green" }
        ]
      },
      { 
        title: "Party Management", 
        text: "Customers and suppliers with contacts and transactions.",
        icon: "users",
        color: "red",
        keywords: ["customers", "suppliers", "contacts", "relationships"],
        metrics: [
          { label: "Customer Management", value: "Complete", color: "red" },
          { label: "Supplier Tracking", value: "Real-time", color: "blue" },
          { label: "Transaction History", value: "Detailed", color: "green" }
        ]
      }
    ],
    testimonials: [
      { 
        initials: "RK", 
        name: "Rajesh Kumar", 
        company: "Kumar Traders", 
        text: "Acctoo has transformed our accounting process.",
        keywords: ["accounting", "transformation", "small business", "traders"],
        rating: 5,
        color: "blue"
      },
      { 
        initials: "PS", 
        name: "Priya Sharma", 
        company: "Sharma Enterprises", 
        text: "The mobile app is fantastic!",
        keywords: ["mobile app", "fantastic", "enterprises", "on-the-go"],
        rating: 5,
        color: "green"
      },
      { 
        initials: "AP", 
        name: "Amit Patel", 
        company: "Patel Manufacturing", 
        text: "Best accounting software for Indian businesses.",
        keywords: ["best", "Indian businesses", "manufacturing", "accounting software"],
        rating: 5,
        color: "purple"
      }
    ],
    faq: [
      { 
        q: "How does the free trial work?", 
        a: "3‑month free trial with full access, no credit card.",
        keywords: ["free trial", "no credit card", "full access", "3 months"]
      },
      { 
        q: "What happens after the trial ends?", 
        a: "₹999/year for Basic; upgrade anytime.",
        keywords: ["pricing", "upgrade", "subscription", "basic plan"]
      },
      { 
        q: "Is my data secure?", 
        a: "Bank‑level encryption, secure cloud, regular backups.",
        keywords: ["security", "encryption", "backups", "data protection"]
      }
    ],
    footer: {
      about: "Smart accounting software designed for Indian businesses.",
      keywords: ["accounting software", "Indian businesses", "smart", "designed"]
    }
  };
  const [content, setContent] = useState(defaultContent);
  const [mode, setMode] = useState('form'); // 'form' | 'json'
  const [activeTab, setActiveTab] = useState('hero');

  // no-op

  const save = async () => { alert('Saved (mock)'); };

  const updateFeature = (index, field, value) => {
    const newFeatures = [...content.featuresGrid];
    newFeatures[index] = { ...newFeatures[index], [field]: value };
    setContent({ ...content, featuresGrid: newFeatures });
  };

  const updateTestimonial = (index, field, value) => {
    const newTestimonials = [...content.testimonials];
    newTestimonials[index] = { ...newTestimonials[index], [field]: value };
    setContent({ ...content, testimonials: newTestimonials });
  };

  const updateFAQ = (index, field, value) => {
    const newFAQ = [...content.faq];
    newFAQ[index] = { ...newFAQ[index], [field]: value };
    setContent({ ...content, faq: newFAQ });
  };

  const addFeature = () => {
    const newFeature = {
      title: "New Feature",
      text: "Feature description",
      icon: "star",
      color: "blue",
      keywords: ["new", "feature"],
      metrics: []
    };
    setContent({ ...content, featuresGrid: [...content.featuresGrid, newFeature] });
  };

  const addTestimonial = () => {
    const newTestimonial = {
      initials: "NT",
      name: "New Customer",
      company: "New Company",
      text: "Great testimonial text",
      keywords: ["new", "customer", "testimonial"],
      rating: 5,
      color: "blue"
    };
    setContent({ ...content, testimonials: [...content.testimonials, newTestimonial] });
  };

  const addFAQ = () => {
    const newFAQ = {
      q: "New Question?",
      a: "New Answer",
      keywords: ["new", "question", "answer"]
    };
    setContent({ ...content, faq: [...content.faq, newFAQ] });
  };

  const removeFeature = (index) => {
    const newFeatures = content.featuresGrid.filter((_, i) => i !== index);
    setContent({ ...content, featuresGrid: newFeatures });
  };

  const removeTestimonial = (index) => {
    const newTestimonials = content.testimonials.filter((_, i) => i !== index);
    setContent({ ...content, testimonials: newTestimonials });
  };

  const removeFAQ = (index) => {
    const newFAQ = content.faq.filter((_, i) => i !== index);
    setContent({ ...content, faq: newFAQ });
  };

  return (
    <div className="p-4 space-y-3">
      <h3 className="text-xl font-semibold">Website Content Editor</h3>
      <div className="flex gap-2 mb-4">
        <button className={`px-3 py-2 border rounded ${mode === 'form' ? 'bg-gray-100' : ''}`} onClick={() => setMode('form')}>Simple Forms</button>
        <button className={`px-3 py-2 border rounded ${mode === 'json' ? 'bg-gray-100' : ''}`} onClick={() => setMode('json')}>Advanced JSON</button>
      </div>

      {mode === 'form' ? (
        <div className="space-y-6">
          {/* Tab Navigation */}
          <div className="flex flex-wrap gap-2 border-b">
            {['hero', 'pricing', 'cta', 'features', 'testimonials', 'faq', 'footer'].map(tab => (
              <button
                key={tab}
                className={`px-4 py-2 border rounded-t ${activeTab === tab ? 'bg-blue-100 border-blue-300' : 'bg-white'}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {/* Hero Section */}
          {activeTab === 'hero' && (
            <div className="p-4 bg-white rounded border">
              <div className="font-semibold mb-4 text-lg">Hero Section</div>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="flex flex-col">
                  <span className="text-sm font-medium mb-1">Title</span>
                  <input 
                    className="border rounded px-3 py-2" 
                    value={content.hero.title} 
                    onChange={(e) => setContent({ ...content, hero: { ...content.hero, title: e.target.value } })} 
                  />
                </label>
                <label className="flex flex-col">
                  <span className="text-sm font-medium mb-1">Highlight Text</span>
                  <input 
                    className="border rounded px-3 py-2" 
                    value={content.hero.highlight} 
                    onChange={(e) => setContent({ ...content, hero: { ...content.hero, highlight: e.target.value } })} 
                  />
                </label>
                <label className="flex flex-col sm:col-span-2">
                  <span className="text-sm font-medium mb-1">Subtitle</span>
                  <textarea 
                    className="border rounded px-3 py-2" 
                    rows={2}
                    value={content.hero.subtitle} 
                    onChange={(e) => setContent({ ...content, hero: { ...content.hero, subtitle: e.target.value } })} 
                  />
                </label>
                <label className="flex flex-col">
                  <span className="text-sm font-medium mb-1">Primary Button Text</span>
                  <input 
                    className="border rounded px-3 py-2" 
                    value={content.hero.primaryCtaText} 
                    onChange={(e) => setContent({ ...content, hero: { ...content.hero, primaryCtaText: e.target.value } })} 
                  />
                </label>
                <label className="flex flex-col">
                  <span className="text-sm font-medium mb-1">Secondary Button Text</span>
                  <input 
                    className="border rounded px-3 py-2" 
                    value={content.hero.secondaryCtaText} 
                    onChange={(e) => setContent({ ...content, hero: { ...content.hero, secondaryCtaText: e.target.value } })} 
                  />
                </label>
              </div>
            </div>
          )}

          {/* Pricing Section */}
          {activeTab === 'pricing' && (
            <div className="p-4 bg-white rounded border">
              <div className="font-semibold mb-4 text-lg">Pricing (Basic Plan)</div>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="flex flex-col">
                  <span className="text-sm font-medium mb-1">Price (₹/year)</span>
                  <input 
                    type="number" 
                    className="border rounded px-3 py-2" 
                    value={content.pricing.basicPrice} 
                    onChange={(e) => setContent({ ...content, pricing: { ...content.pricing, basicPrice: Number(e.target.value) } })} 
                  />
                </label>
                <label className="flex flex-col">
                  <span className="text-sm font-medium mb-1">Trial Text</span>
                  <input 
                    className="border rounded px-3 py-2" 
                    value={content.pricing.trialText} 
                    onChange={(e) => setContent({ ...content, pricing: { ...content.pricing, trialText: e.target.value } })} 
                  />
                </label>
              </div>
            </div>
          )}

          {/* CTA Section */}
          {activeTab === 'cta' && (
            <div className="p-4 bg-white rounded border">
              <div className="font-semibold mb-4 text-lg">Call to Action Section</div>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="flex flex-col">
                  <span className="text-sm font-medium mb-1">Heading</span>
                  <input 
                    className="border rounded px-3 py-2" 
                    value={content.cta.heading} 
                    onChange={(e) => setContent({ ...content, cta: { ...content.cta, heading: e.target.value } })} 
                  />
                </label>
                <label className="flex flex-col">
                  <span className="text-sm font-medium mb-1">Subheading</span>
                  <input 
                    className="border rounded px-3 py-2" 
                    value={content.cta.subheading} 
                    onChange={(e) => setContent({ ...content, cta: { ...content.cta, subheading: e.target.value } })} 
                  />
                </label>
                <label className="flex flex-col">
                  <span className="text-sm font-medium mb-1">Primary Button Text</span>
                  <input 
                    className="border rounded px-3 py-2" 
                    value={content.cta.primaryCtaText} 
                    onChange={(e) => setContent({ ...content, cta: { ...content.cta, primaryCtaText: e.target.value } })} 
                  />
                </label>
                <label className="flex flex-col">
                  <span className="text-sm font-medium mb-1">Secondary Button Text</span>
                  <input 
                    className="border rounded px-3 py-2" 
                    value={content.cta.secondaryCtaText} 
                    onChange={(e) => setContent({ ...content, cta: { ...content.cta, secondaryCtaText: e.target.value } })} 
                  />
                </label>
              </div>
            </div>
          )}

          {/* Features Section */}
          {activeTab === 'features' && (
            <div className="p-4 bg-white rounded border">
              <div className="flex justify-between items-center mb-4">
                <div className="font-semibold text-lg">Feature Cards</div>
                <button className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700" onClick={addFeature}>
                  + Add Feature
                </button>
              </div>
              <div className="space-y-4">
                {content.featuresGrid.map((feature, index) => (
                  <div key={index} className="border rounded p-4 space-y-3">
                    <div className="flex justify-between items-start">
                      <h4 className="font-medium">Feature {index + 1}</h4>
                      <button 
                        className="text-red-600 hover:text-red-800" 
                        onClick={() => removeFeature(index)}
                      >
                        Remove
                      </button>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <label className="flex flex-col">
                        <span className="text-sm font-medium mb-1">Title</span>
                        <input 
                          className="border rounded px-3 py-2" 
                          value={feature.title} 
                          onChange={(e) => updateFeature(index, 'title', e.target.value)} 
                        />
                      </label>
                      <label className="flex flex-col">
                        <span className="text-sm font-medium mb-1">Icon</span>
                        <input 
                          className="border rounded px-3 py-2" 
                          value={feature.icon} 
                          onChange={(e) => updateFeature(index, 'icon', e.target.value)} 
                        />
                      </label>
                      <label className="flex flex-col">
                        <span className="text-sm font-medium mb-1">Color</span>
                        <select 
                          className="border rounded px-3 py-2" 
                          value={feature.color} 
                          onChange={(e) => updateFeature(index, 'color', e.target.value)}
                        >
                          {['blue', 'green', 'purple', 'orange', 'indigo', 'red', 'pink', 'yellow'].map(color => (
                            <option key={color} value={color}>{color}</option>
                          ))}
                        </select>
                      </label>
                      <label className="flex flex-col">
                        <span className="text-sm font-medium mb-1">Keywords (comma separated)</span>
                        <input 
                          className="border rounded px-3 py-2" 
                          value={feature.keywords.join(', ')} 
                          onChange={(e) => updateFeature(index, 'keywords', e.target.value.split(',').map(k => k.trim()))} 
                        />
                      </label>
                      <label className="flex flex-col sm:col-span-2">
                        <span className="text-sm font-medium mb-1">Description</span>
                        <textarea 
                          className="border rounded px-3 py-2" 
                          rows={2}
                          value={feature.text} 
                          onChange={(e) => updateFeature(index, 'text', e.target.value)} 
                        />
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Testimonials Section */}
          {activeTab === 'testimonials' && (
            <div className="p-4 bg-white rounded border">
              <div className="flex justify-between items-center mb-4">
                <div className="font-semibold text-lg">Customer Testimonials</div>
                <button className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700" onClick={addTestimonial}>
                  + Add Testimonial
                </button>
              </div>
              <div className="space-y-4">
                {content.testimonials.map((testimonial, index) => (
                  <div key={index} className="border rounded p-4 space-y-3">
                    <div className="flex justify-between items-start">
                      <h4 className="font-medium">Testimonial {index + 1}</h4>
                      <button 
                        className="text-red-600 hover:text-red-800" 
                        onClick={() => removeTestimonial(index)}
                      >
                        Remove
                      </button>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <label className="flex flex-col">
                        <span className="text-sm font-medium mb-1">Initials</span>
                        <input 
                          className="border rounded px-3 py-2" 
                          value={testimonial.initials} 
                          onChange={(e) => updateTestimonial(index, 'initials', e.target.value)} 
                        />
                      </label>
                      <label className="flex flex-col">
                        <span className="text-sm font-medium mb-1">Name</span>
                        <input 
                          className="border rounded px-3 py-2" 
                          value={testimonial.name} 
                          onChange={(e) => updateTestimonial(index, 'name', e.target.value)} 
                        />
                      </label>
                      <label className="flex flex-col">
                        <span className="text-sm font-medium mb-1">Company</span>
                        <input 
                          className="border rounded px-3 py-2" 
                          value={testimonial.company} 
                          onChange={(e) => updateTestimonial(index, 'company', e.target.value)} 
                        />
                      </label>
                      <label className="flex flex-col">
                        <span className="text-sm font-medium mb-1">Color</span>
                        <select 
                          className="border rounded px-3 py-2" 
                          value={testimonial.color} 
                          onChange={(e) => updateTestimonial(index, 'color', e.target.value)}
                        >
                          {['blue', 'green', 'purple', 'orange', 'indigo', 'red', 'pink', 'yellow'].map(color => (
                            <option key={color} value={color}>{color}</option>
                          ))}
                        </select>
                      </label>
                      <label className="flex flex-col">
                        <span className="text-sm font-medium mb-1">Rating</span>
                        <select 
                          className="border rounded px-3 py-2" 
                          value={testimonial.rating} 
                          onChange={(e) => updateTestimonial(index, 'rating', Number(e.target.value))}
                        >
                          {[1, 2, 3, 4, 5].map(rating => (
                            <option key={rating} value={rating}>{rating} stars</option>
                          ))}
                        </select>
                      </label>
                      <label className="flex flex-col">
                        <span className="text-sm font-medium mb-1">Keywords (comma separated)</span>
                        <input 
                          className="border rounded px-3 py-2" 
                          value={testimonial.keywords.join(', ')} 
                          onChange={(e) => updateTestimonial(index, 'keywords', e.target.value.split(',').map(k => k.trim()))} 
                        />
                      </label>
                      <label className="flex flex-col sm:col-span-2">
                        <span className="text-sm font-medium mb-1">Testimonial Text</span>
                        <textarea 
                          className="border rounded px-3 py-2" 
                          rows={2}
                          value={testimonial.text} 
                          onChange={(e) => updateTestimonial(index, 'text', e.target.value)} 
                        />
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* FAQ Section */}
          {activeTab === 'faq' && (
            <div className="p-4 bg-white rounded border">
              <div className="flex justify-between items-center mb-4">
                <div className="font-semibold text-lg">Frequently Asked Questions</div>
                <button className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700" onClick={addFAQ}>
                  + Add FAQ
                </button>
              </div>
              <div className="space-y-4">
                {content.faq.map((item, index) => (
                  <div key={index} className="border rounded p-4 space-y-3">
                    <div className="flex justify-between items-start">
                      <h4 className="font-medium">FAQ {index + 1}</h4>
                      <button 
                        className="text-red-600 hover:text-red-800" 
                        onClick={() => removeFAQ(index)}
                      >
                        Remove
                      </button>
                    </div>
                    <div className="grid gap-3">
                      <label className="flex flex-col">
                        <span className="text-sm font-medium mb-1">Question</span>
                        <input 
                          className="border rounded px-3 py-2" 
                          value={item.q} 
                          onChange={(e) => updateFAQ(index, 'q', e.target.value)} 
                        />
                      </label>
                      <label className="flex flex-col">
                        <span className="text-sm font-medium mb-1">Answer</span>
                        <textarea 
                          className="border rounded px-3 py-2" 
                          rows={2}
                          value={item.a} 
                          onChange={(e) => updateFAQ(index, 'a', e.target.value)} 
                        />
                      </label>
                      <label className="flex flex-col">
                        <span className="text-sm font-medium mb-1">Keywords (comma separated)</span>
                        <input 
                          className="border rounded px-3 py-2" 
                          value={item.keywords.join(', ')} 
                          onChange={(e) => updateFAQ(index, 'keywords', e.target.value.split(',').map(k => k.trim()))} 
                        />
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footer Section */}
          {activeTab === 'footer' && (
            <div className="p-4 bg-white rounded border">
              <div className="font-semibold mb-4 text-lg">Footer</div>
              <div className="space-y-4">
                <label className="flex flex-col">
                  <span className="text-sm font-medium mb-1">About Text</span>
                  <textarea 
                    className="border rounded px-3 py-2" 
                    rows={2}
                    value={content.footer.about} 
                    onChange={(e) => setContent({ ...content, footer: { ...content.footer, about: e.target.value } })} 
                  />
                </label>
                <label className="flex flex-col">
                  <span className="text-sm font-medium mb-1">Keywords (comma separated)</span>
                  <input 
                    className="border rounded px-3 py-2" 
                    value={content.footer.keywords.join(', ')} 
                    onChange={(e) => setContent({ ...content, footer: { ...content.footer, keywords: e.target.value.split(',').map(k => k.trim()) } })} 
                  />
                </label>
              </div>
            </div>
          )}

          {/* Save Button */}
          <div className="flex justify-end">
            <button className="px-6 py-3 bg-green-600 text-white rounded hover:bg-green-700" onClick={save} disabled={loading}>
              {loading ? 'Saving...' : 'Save All Changes'}
            </button>
          </div>
        </div>
      ) : (
        <div className="p-4 bg-white rounded border">
          <label className="block text-sm text-gray-600 mb-2">Advanced JSON Editor</label>
          <textarea 
            className="w-full border rounded px-3 py-2" 
            rows={20} 
            value={JSON.stringify(content, null, 2)} 
            onChange={(e) => {
              try { setContent(JSON.parse(e.target.value)); } catch (_) { /* ignore */ }
            }} 
          />
          <div className="mt-3">
            <button className="px-3 py-2 border rounded" onClick={save} disabled={loading}>
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const SubscriptionsSection = () => (
  <div className="p-6">
    <div className="bg-white rounded-lg border p-6">
      <div className="flex items-center gap-3 mb-2">
        <Settings className="w-5 h-5 text-teal-600" />
        <h3 className="text-xl font-semibold">Subscriptions</h3>
      </div>
      <p className="text-gray-600">Manage subscriptions here after enabling cloud functions.</p>
    </div>
  </div>
);

const BackofficeContent = ({ role }) => {
  const [active, setActive] = useState('dashboard');
  return (
    <div className="flex flex-col md:flex-row">
      <Sidebar active={active} onChange={setActive} role={role} />
      <div className="flex-1 min-h-screen bg-gray-50">
        {active === 'dashboard' && <AdminDashboard onNavigate={setActive} />}
        {active === 'admins' && <AdminDirectory />}
        {active === 'users' && <UsersSection />}
        {active === 'companies' && <CompanyDirectory />}
        {active === 'subscription-manager' && <SubscriptionManager />}
        {active === 'plan-catalog' && <PlanCatalogManager />}
        {active === 'topup-manager' && <TopupManager />}
        {active === 'plan-upgrade' && <PlanUpgrade />}
        {active === 'payment-recorder' && <PaymentRecorder />}
        {active === 'payment-history' && <PaymentHistory />}
        {active === 'subscription-migration' && <SubscriptionMigration />}
        {active === 'collection-initializer' && <CollectionInitializer />}
        {active === 'activity-logs' && <ActivityLogs />}
        {active === 'coupons' && <CouponsManager />}
        {active === 'allocations' && <AllocationsManager role={role} onNavigate={setActive} />}
        {active === 'public-content' && <PublicContentManager />}
        {active === 'blog-content' && <BlogContentManager />}
        {active === 'logs' && <AdminAuditLogs />}
        {active === 'api-settings' && <ApiSettings role={role} />}
      </div>
    </div>
  );
};

const Backoffice = () => {
  const [admin, setAdmin] = useState(null);
  const [isAdminDoc, setIsAdminDoc] = useState(null); // null=checking, true/false
  const [role, setRole] = useState(null);

  // Only show superadmin creation in development - hide on production domains
  const isProduction = window.location.hostname === 'backoffice.acctoo.com' || 
                       window.location.hostname === 'backoffice-acctoo.web.app';
  const isDevelopment = !isProduction;

  useEffect(() => {
    const unsub = onAuthStateChanged(backofficeAuth, (u) => {
      setAdmin(u ? { uid: u.uid, email: u.email } : null);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const check = async () => {
      if (!admin) { setIsAdminDoc(null); return; }
      const ref = doc(backofficeDb, 'admins', admin.uid);
      const snap = await getDoc(ref);
      setIsAdminDoc(snap.exists());
      setRole(snap.exists() ? (snap.data()?.role || 'read_only') : null);
    };
    check();
  }, [admin]);

  if (!admin) {
    return <Login onAuthed={(u) => setAdmin(u)} />;
  }

  if (isAdminDoc === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-gray-600">Checking admin access…</div>
    );
  }

  if (isAdminDoc === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="w-full max-w-md bg-white border rounded-xl p-6">
          <div className="text-lg font-semibold">Admin approval required</div>
          <div className="text-sm text-gray-600 mt-1">This account is not registered as a backoffice admin yet.</div>
          {isDevelopment && (
            <>
              <button
                className="mt-4 px-3 py-2 bg-blue-600 text-white rounded"
                onClick={async () => {
                  const ref = doc(backofficeDb, 'admins', admin.uid);
                  await setDoc(ref, { email: admin.email, role: 'super_admin', createdAt: serverTimestamp() }, { merge: true });
                  setIsAdminDoc(true);
                }}
              >Make me Superadmin</button>
              <div className="mt-3 text-xs text-gray-500">You can change role later in Admins settings.</div>
            </>
          )}
          {!isDevelopment && (
            <div className="mt-4 text-sm text-gray-600">
              Please contact an existing administrator to grant you access.
            </div>
          )}
          <div className="mt-4">
            <button className="border rounded px-2 py-1" onClick={() => signOut(backofficeAuth)}>Logout</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LogIn className="w-5 h-5 text-blue-600" />
            <div className="text-lg font-semibold">ACCTOO Backoffice</div>
          </div>
          <div className="text-sm text-gray-600 flex items-center gap-3">
            <span>{admin.email}</span>
            <button className="border rounded px-2 py-1" onClick={() => signOut(backofficeAuth)}>Logout</button>
          </div>
        </div>
      </div>
      <BackofficeContent role={role} />
    </div>
  );
};

export default Backoffice;


